import config from '../../../config';
import { addDistanceToBounds, gmLatLng } from '../../../util/googleMaps';
import { getCurrentUserLocation } from '../../../util/localStorage';

const { listingTypeHorse } = config;

export const formatNumber = number => {
    return new Intl.NumberFormat('ch-CH', {
        minimumFractionDigits: 0,
    }).format(number);
};

/**
 * @param lat
 * @param long
 * @param radius in km
 * @returns {string|null}
 */
export const addDistanceToCoordinates = (lat, long, radius = 30) => {
    try {
        if (!lat || !long) return null;

        const bounds = addDistanceToBounds(gmLatLng(lat, long), radius * 1000);

        return `${bounds.ne.lat},${bounds.ne.lng},${bounds.sw.lat},${bounds.sw.lng}`;
    } catch (e) {
        return null;
    }
};

// Panel width relative to the viewport
export const panelMediumWidth = 50;
export const panelLargeWidth = 62.5;
export const cardRenderSizes = [
    '(max-width: 767px) 100vw',
    `(max-width: 1023px) ${panelMediumWidth}vw`,
    `(max-width: 1920px) ${panelLargeWidth / 2}vw`,
    `${panelLargeWidth / 3}vw`,
].join(', ');

export const allowedCountries = ['Schweiz', 'Deutschland'];

export const noIndexTag = [
    {
        name: 'robots',
        content: 'noindex',
    },
];

export const getCenterData = ({ landingData, country, city, canton }) => {
    let center = '';
    if (city) {
        center = `${landingData.postcode} ${landingData.name},${landingData?.canton?.name},${country}`;
    } else if (canton && country === 'Schweiz') {
        center = `Kanton ${landingData.name},${country}`;
    } else if (canton && country === 'Deutschland') {
        center = `${landingData.name},${country}`;
    } else if (country) {
        center = country;
    }

    return center;
};

export const getMapUrl = ({ landingData, country, city, canton }) => {
    const center = landingData ? getCenterData({ landingData, country, city, canton }) : '';

    return `https://maps.googleapis.com/maps/api/staticmap?center=${encodeURIComponent(
        center
    )}&size=640x387&scale=2&maptype=roadmap&key=${
        config.maps.googleMapsAPIKey
    }&style=feature:poi|visibility:off&style=feature:road|element:labels.icon|visibility:off&style=feature:transit|visibility:off`;
};

export const getMapAltText = ({ landingData, country, city, canton }) => {
    const staticMapAltAttribute = 'Statische Karte von';

    if (city) {
        return `${staticMapAltAttribute} ${landingData.postcode} ${landingData.name}, ${country}`;
    } else if (canton && country === 'Schweiz') {
        return `${staticMapAltAttribute} Kanton ${landingData.name}, ${country}`;
    } else if (canton && country === 'Deutschland') {
        return `${staticMapAltAttribute} ${landingData.name}, ${country}`;
    } else if (country) {
        return `${staticMapAltAttribute} ${country}`;
    } else {
        return staticMapAltAttribute.replace(' von', '');
    }
};

export const calculateBoundsFromLPData = ({ landingData, city }) =>
    !city
        ? landingData?.bounds || ''
        : landingData?.coordinates
        ? addDistanceToCoordinates(
              landingData.coordinates.coordinates[0],
              landingData.coordinates.coordinates[1]
          )
        : '';

export const isPublishedHorseListing = l =>
    l.attributes.state === 'published' && l.attributes?.publicData?.type === listingTypeHorse;

export const getLocationFromCoordinates = landingData => {
    if (landingData?.coordinates) {
        const lat = landingData.coordinates.coordinates[0];
        const long = landingData.coordinates.coordinates[1];
        return `${lat},${long}`;
    } else {
        const { loc } = getCurrentUserLocation();
        return loc;
    }
};

export const generateListingData = ({
    listings1to2,
    listings2to3,
    listings3to5,
    shouldFetchPerDayListings,
    address,
    bounds,
    origin,
    pubActivities,
    listings,
}) => ({
    '1-2': {
        listingData: shouldFetchPerDayListings ? listings1to2 || [] : [],
        link: `?address=${address}&bounds=${bounds}&origin=${origin}&listingType=${listingTypeHorse}&pub_availability=1-2&pub_activities=has_any%3A${pubActivities}`,
    },
    '2-3': {
        listingData: shouldFetchPerDayListings ? listings2to3 || [] : [],
        link: `?address=${address}&bounds=${bounds}&origin=${origin}&listingType=${listingTypeHorse}&pub_availability=2-3&pub_activities=has_any%3A${pubActivities}`,
    },
    '3-5': {
        listingData: shouldFetchPerDayListings ? listings3to5 || [] : [],
        link: `?address=${address}&bounds=${bounds}&origin=${origin}&listingType=${listingTypeHorse}&pub_availability=3-5&pub_activities=has_any%3A${pubActivities}`,
    },
    listings: {
        listingData: listings || [],
        link: `?address=${address}&bounds=${bounds}&origin=${origin}&listingType=${listingTypeHorse}&pub_activities=has_any%3A${pubActivities}`,
    },
});

export const extractListingFromStore = (state, availability, activitiesArr) => {
    const listings = state.marketplaceData.entities?.listing;

    const checkPublicData = listing =>
        listing.attributes?.publicData?.availability === availability &&
        activitiesArr.some(activity =>
            listing.attributes?.publicData?.activities?.includes(activity)
        );

    return Object.values(listings || {}).filter(
        listing => isPublishedHorseListing(listing) && checkPublicData(listing)
    );
};
